'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { Suspense } from 'react'
import Image from 'next/image'
import { CheckCircle2, Loader2, ShieldX, UserRound } from 'lucide-react'
import { toast } from 'sonner'
import { clearSessionLocalState } from '@/lib/clear-session-local-state'

// Friendly notices surfaced via /login?notice=<code>. Used by the Keycloak
// info.ftl auto-redirect after UPDATE_PASSWORD + UPDATE_PROFILE complete
// (Issues #3 + #4 / ASC-4642 + ASC-4643). When notice is present we suppress
// the auto-bounce to /api/auth/start so the user can read the message and
// sign in manually with their new password.
const NOTICE_MESSAGES: Record<string, string> = {
  'password-updated': 'Your details have been updated. Please sign in with your new password.',
}

// Friendly messages for OIDC failure modes surfaced via /login?error=<code>.
// Codes come from /api/auth/callback redirects (ASC-3895 §13.14 auth UX hardening).
const ERROR_MESSAGES: Record<string, { title: string; body: string }> = {
  flow_lost: {
    // This code covers three structurally different causes — real flow-cookie
    // TTL expiry, a duplicate /api/auth/start race (login/page.tsx redirect
    // guard), and a concurrent tab overwriting the shared oidc_state/verifier
    // cookies (auth/start/route.ts) — and the backend has no way to tell them
    // apart from the callback alone. Earlier copy asserted "took too long" as
    // the cause, which is simply false for the other two. Kept deliberately
    // neutral rather than guessing. Ref: ASC-7678 (ASC-6810).
    title: "We couldn't finish signing you in",
    body: 'This can happen if the sign-in page sat open too long, was started in another browser tab, or the page was reloaded mid-flow. Click "Sign in" to try again.',
  },
  // ASC-8271 split flow_lost into per-cause codes. The entry above is retained
  // for URLs already in flight (a reloaded error page, a bookmark) — the
  // callback no longer emits it.
  flow_replaced: {
    // Deliberately does NOT say "expired": other flow cookies were live but
    // none matched the returned state. Since ASC-10125 a second tab alone no
    // longer causes this — only more than MAX_CONCURRENT_FLOWS sign-ins
    // (oldest evicted) or a genuine CSRF attempt, so the copy stays neutral.
    title: 'Your sign-in was replaced by a newer one',
    body: 'Sign-in was started more than once — usually another browser tab. Close the other tabs and click "Sign in" to try again.',
  },
  flow_partial: {
    title: "We couldn't finish signing you in",
    body: 'Part of the sign-in data did not survive the round trip to the identity provider. This is usually a browser cookie problem — clear cookies for this site and try again.',
  },
  missing_code: {
    title: 'Sign-in was interrupted',
    body: 'We did not receive a response from the identity provider. Try signing in again.',
  },
  keycloak_error: {
    title: 'Identity provider could not complete sign-in',
    body: 'Something went wrong while exchanging your credentials. Try signing in again, or contact your administrator if this persists.',
  },
  recovery_loop: {
    title: 'We could not recover your sign-in',
    body: 'Sign-in failed twice in a row. Please clear cookies for this site and try again, or contact your administrator.',
  },
  domain_not_allowed: {
    title: 'Email domain not allowed',
    body: 'Your email domain is not on the allow list for this environment. Contact your administrator.',
  },
  platform_admin_required: {
    title: 'Platform admin access required',
    body: 'You signed in successfully but do not have platform-admin privileges.',
  },
  token_exchange_failed: {
    title: 'Identity provider could not complete sign-in',
    body: 'The token exchange with the identity provider failed. Try again, or contact your administrator.',
  },
}

// The reuse confirmation (ASC-8200) names the account via a short-lived, readable
// `signin_hint` cookie the callback set — the session itself is NOT yet minted, so
// there is no authenticated endpoint to ask. Returns null when absent/unreadable.
function readSigninHint(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)signin_hint=([^;]*)/)
  if (!match) return null
  try {
    return decodeURIComponent(match[1]) || null
  } catch {
    return null
  }
}

function describeError(code: string, fallback: string | null): { title: string; body: string } {
  return (
    ERROR_MESSAGES[code] ?? {
      title: 'Authentication error',
      body: fallback ?? code,
    }
  )
}

function LoginRedirect() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirect = searchParams.get('redirect') ?? '/'
  const error = searchParams.get('error')
  const errorDescription = searchParams.get('error_description')
  const notice = searchParams.get('notice')
  const noticeMessage = notice ? NOTICE_MESSAGES[notice] : null
  const noticeToastFired = useRef(false)
  // ASC-7207 — /api/auth/start sends us back here with signed_in=1 when a valid
  // session already exists but nobody has said whose it is. Suppresses the
  // auto-bounce below, so the two intents (resume vs. a different person
  // signing in) are chosen explicitly instead of guessed from a cookie.
  const alreadySignedIn = searchParams.get('signed_in') === '1'
  // ASC-8200 — set when the confirmation follows a Keycloak SSO *reuse* (callback
  // minted no session yet, so "Continue" must re-enter /api/auth/start to mint it)
  // rather than an existing Scorpio session (ASC-7207, where resume suffices).
  const reuseConfirm = searchParams.get('reuse') === '1'
  // Read the account name in an effect, not during render: readSigninHint() reads
  // document.cookie, which is absent during the dynamic SSR of /login, so computing
  // it inline would render the generic sentence server-side and flash to "continue
  // as <name>" on hydration (a React mismatch on the one screen whose job is to name
  // the account). State starts null → matches the server → fills on the next tick.
  const [signinHint, setSigninHint] = useState<string | null>(null)
  useEffect(() => {
    // set-state-in-effect intentionally — document.cookie is a browser-only value
    // that MUST be read after hydration, not during render, to avoid an SSR
    // mismatch on this screen's heading. It fires once per mount, not in a loop.
    // Ref: ASC-10386 (galliumsoft review, login:115).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (reuseConfirm) setSigninHint(readSigninHint())
  }, [reuseConfirm])
  // ASC-5952 — the callback got a fresh Keycloak login it could not complete
  // while another session is live (browser Back onto a stale authorize URL).
  const identityChanged = searchParams.get('reason') === 'identity_changed'
  // Idempotency guard on the redirect to /api/auth/start (ASC-6810).
  //
  // Assigning window.location.href schedules a navigation — it does not stop
  // the current page from running. Any re-invocation before the browser
  // actually leaves (a re-render, StrictMode's double effect, a fast repeat
  // click) issues a SECOND /api/auth/start, which mints a second oidc_state
  // cookie that silently overwrites the first: same name, same path, so
  // last-write-wins. The user then completes login against whichever
  // Keycloak page they are looking at, the callback compares its incoming
  // `state` against the surviving cookie, they don't match, and the user is
  // bounced back to /login with flow_replaced — the "had to sign in twice"
  // symptom, independent of any cookie TTL. Observed live as a genuine
  // duplicate /api/auth/start within one page load (ASC-5617 investigation).
  // Since ASC-10125 a duplicate mints its own per-flow cookie instead of
  // overwriting, so this guard now only saves a wasted flow.
  const redirectStarted = useRef(false)

  // ASC-10715 — every way a session ends lands here: the logout action, the client
  // refresh/session-probe bounces in AuthzenProvider, and the server redirects in
  // proxy.ts and /api/auth/refresh (which run no client code, so this is their only
  // hook). Drops the per-agent last-workspace entries so the next session starts on the
  // preferred workspace. Skipped on signed_in=1: that page is shown for a session that is
  // still valid, and "resume" must keep it. A deliberate visit to /login on a live
  // session does clear — it only costs a fall back to the preferred workspace.
  useEffect(() => {
    if (alreadySignedIn) return
    clearSessionLocalState()
  }, [alreadySignedIn])

  useEffect(() => {
    // Issue #3 / ASC-4642 — when a notice is set (e.g., from the Keycloak
    // info.ftl auto-redirect after UPDATE_PASSWORD + UPDATE_PROFILE), show
    // a toast once. We don't strip the param: the auto-bounce guard below
    // depends on it staying readable so the user can land on /login,
    // read the message, and sign in manually.
    if (noticeMessage && !noticeToastFired.current) {
      toast.success(noticeMessage, { duration: 8000 })
      noticeToastFired.current = true
    }
  }, [noticeMessage])

  useEffect(() => {
    if (error) return
    // Auto-bounce guard: when the user just completed Keycloak required
    // actions, suppress the auto-redirect to /api/auth/start. The user
    // needs to sign in with their NEW password — bouncing them through
    // Keycloak with a stale (or absent) SSO session would be confusing.
    // Ref: ASC-4642 / ASC-4643.
    if (noticeMessage) return
    // ASC-7207 — /api/auth/start bounced us back because a session already
    // exists. Bouncing straight back would loop; the interstitial below asks
    // whose session it is instead.
    if (alreadySignedIn) return
    // /api/auth/start checks server-side whether a valid session already
    // exists (access_token is HttpOnly, so that check can only run there —
    // not here via document.cookie) and redirects straight to the app
    // without touching Keycloak when it does. Ref: ASC-5993, ASC-3895.
    if (redirectStarted.current) return
    redirectStarted.current = true
    const params = new URLSearchParams({ redirect })
    window.location.href = `/api/auth/start?${params.toString()}`
  }, [error, redirect, noticeMessage, alreadySignedIn])

  if (alreadySignedIn && !error) {
    // ASC-7207 — a valid session exists on this browser, but we do not know
    // whether the person in front of it is the one who created it. Ask.
    //
    // Both buttons go back through /api/auth/start rather than navigating
    // straight into the app: the access_token is HttpOnly, so only the server
    // can act on it, and routing both intents through one place keeps the
    // session-clearing on the switch path impossible to forget.
    // On the reuse path the callback minted NO session (it set a readable
    // signin_hint naming the account instead), so "Continue" must re-enter
    // /api/auth/start to mint it (reuse_confirm). The ASC-7207 path already has a
    // live session, so resume suffices there. `signinHint` is filled by the effect
    // above, not read during render (see the SSR note on its declaration).
    const handleContinue = () => {
      if (redirectStarted.current) return
      redirectStarted.current = true
      const params = new URLSearchParams({ redirect })
      params.set(reuseConfirm ? 'reuse_confirm' : 'resume', '1')
      window.location.href = `/api/auth/start?${params.toString()}`
    }
    const handleSwitchUser = () => {
      if (redirectStarted.current) return
      redirectStarted.current = true
      const params = new URLSearchParams({ redirect, switch_user: '1' })
      window.location.href = `/api/auth/start?${params.toString()}`
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-4">
        <Image width={128} height={32} src="/images/oraczen-logo.svg" alt="Oraczen" priority />
        <Image
          width={192}
          height={48}
          src="/images/scorpio-logo-black.svg"
          alt="Scorpio"
          priority
        />
        <div className="flex flex-col items-center gap-3 bg-muted border border-border rounded-lg px-6 py-5 max-w-sm w-full text-center">
          <UserRound className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">
            {signinHint
              ? `You're about to continue as ${signinHint}`
              : identityChanged
                ? 'A different account is still signed in on this browser'
                : "You're already signed in on this browser"}
          </p>
          <p className="text-sm text-muted-foreground">
            {signinHint
              ? 'If this is not you, sign in as a different user.'
              : identityChanged
                ? 'Your sign-in could not be completed because another session is active. Sign in again as yourself, or continue with the existing session.'
                : 'Continue with that account, or sign in as someone else.'}
          </p>
        </div>
        <div className="flex flex-col gap-2 max-w-sm w-full">
          <button
            type="button"
            onClick={handleContinue}
            className={
              identityChanged
                ? 'rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors'
                : 'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors'
            }
          >
            {signinHint ? `Continue as ${signinHint}` : 'Continue'}
          </button>
          <button
            type="button"
            onClick={handleSwitchUser}
            className={
              identityChanged
                ? 'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors'
                : 'rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors'
            }
          >
            Sign in as a different user
          </button>
        </div>
      </div>
    )
  }

  if (noticeMessage && !error) {
    // Static "ready to sign in" page — the toast above carries the message
    // about updated details. The Sign-in button restarts the OIDC flow
    // without the notice param so the auto-bounce guard releases.
    const handleSignIn = () => {
      if (redirectStarted.current) return
      redirectStarted.current = true
      const params = new URLSearchParams({ redirect })
      window.location.href = `/api/auth/start?${params.toString()}`
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-4">
        <Image width={128} height={32} src="/images/oraczen-logo.svg" alt="Oraczen" priority />
        <Image
          width={192}
          height={48}
          src="/images/scorpio-logo-black.svg"
          alt="Scorpio"
          priority
        />
        <div className="flex flex-col items-center gap-3 bg-success/10 border border-success/20 rounded-lg px-6 py-5 max-w-sm w-full text-center">
          <CheckCircle2 className="h-6 w-6 text-success" aria-hidden="true" />
          <p className="text-sm font-medium">Your details have been updated</p>
          <p className="text-sm text-muted-foreground">
            Please sign in with your new password to continue.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSignIn}
          aria-label="Sign in"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Sign in
        </button>
      </div>
    )
  }

  if (error) {
    const { title, body } = describeError(error, errorDescription)
    const handleRetry = () => {
      // Explicit release, not a stale carry-over: router.push to /login (the
      // route we are already on) re-renders this component rather than
      // remounting it, so the ref survives. Leaving it true would make the
      // auto-bounce effect below no-op on every subsequent attempt and strand
      // the user on the error card with a dead "Sign in" button.
      redirectStarted.current = false
      const params = new URLSearchParams({ redirect })
      router.push(`/login?${params.toString()}`)
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6 px-4">
        <Image width={128} height={32} src="/images/oraczen-logo.svg" alt="Oraczen" priority />
        <Image
          width={192}
          height={48}
          src="/images/scorpio-logo-black.svg"
          alt="Scorpio"
          priority
        />
        <div className="flex flex-col items-center gap-3 bg-destructive/10 border border-destructive/20 rounded-lg px-6 py-5 max-w-sm w-full text-center">
          <ShieldX className="h-6 w-6 text-destructive" aria-hidden="true" />
          <p className="text-sm font-medium text-destructive">{title}</p>
          <p className="text-sm text-muted-foreground">{body}</p>
          <p className="text-xs text-muted-foreground font-mono">code: {error}</p>
        </div>
        <button
          type="button"
          onClick={handleRetry}
          aria-label="Sign in again"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Sign in
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-6">
      <Image width={128} height={32} src="/images/oraczen-logo.svg" alt="Oraczen" priority />
      <Image width={192} height={48} src="/images/scorpio-logo-black.svg" alt="Scorpio" priority />
      <p className="textBaseLeadingNormalRegular text-muted-foreground text-center max-w-sm">
        Supply Chain Optimization &amp; Real-time Planning Intelligence Orchestrator
      </p>
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
      <p className="textSmallLeadingNormalRegular text-muted-foreground" role="status">
        Redirecting…
      </p>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginRedirect />
    </Suspense>
  )
}
